import { useEffect, useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { VersionSelector } from "@/components/domain/version-selector"
import { VersionTimeline } from "@/components/domain/version-timeline"
import { TestLabTabs } from "@/components/domain/test-lab-tabs"
import { ScenarioList } from "@/components/domain/scenario-list"
import { ScenarioDetail } from "@/components/domain/scenario-detail"
import { RehearsalCardCompact } from "@/components/domain/rehearsal-card-compact"
import { DesignerPane } from "@/components/domain/designer-pane"
import { EditHistoryPane } from "@/components/domain/edit-history-pane"
import { TestPane } from "@/components/domain/test-pane"
import { VersionsTable } from "@/components/domain/versions-table"
import type { TestLabProps, TestLabTab } from "@/types/test_lab"

export default function TestLab({ skill, versions, agent, agents, tab, scenarios, promote, initial_source_key, test_stream }: TestLabProps) {
  // flash is rendered globally by FlashBanner inside AppShell — pulling
  // it here duplicated the toast.

  // Three sub-tabs:
  //   Design — DesignerPane (Forge co-edit)
  //   Chat   — TestPane (live free-form chat with the agent)
  //   Test   — the redesigned scenario suite (formerly "Replay & Assertions")
  const initialTab: TestLabTab =
    tab === "design" ? "design" :
    tab === "chat"   ? "chat"   : "test"
  const [activeTab, setActiveTab]             = useState<TestLabTab>(initialTab)
  const [selectedVersion, setSelectedVersion] = useState<number>(skill.version)
  const [seedMessage, setSeedMessage]         = useState<string | null>(null)
  const [seedNonce, setSeedNonce]             = useState(0)

  // Suite-side state: which scenario card is selected, which scenarios are
  // mid-Run-start (optimistic spinner before the cable update arrives), and
  // whether Run-all is in flight.
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(scenarios[0]?.id ?? null)
  const [busyScenarioIds, setBusyScenarioIds] = useState<Set<number>>(new Set())
  const [runAllBusy, setRunAllBusy] = useState(false)
  const [rehearsalStarting, setRehearsalStarting] = useState(false)
  const [rehearsalRerunning, setRehearsalRerunning] = useState(false)

  const selectedScenario = useMemo(
    () => scenarios.find((s) => s.id === selectedScenarioId) ?? null,
    [scenarios, selectedScenarioId]
  )

  // If the selected scenario disappears (e.g., agent picker change), fall
  // back to the first one.
  useEffect(() => {
    if (selectedScenarioId && !scenarios.find((s) => s.id === selectedScenarioId)) {
      setSelectedScenarioId(scenarios[0]?.id ?? null)
    }
  }, [scenarios, selectedScenarioId])

  // RunOneWorker → broadcasts → Inertia partial reload → fresh `scenarios`.
  // Clear the optimistic-busy set on every refresh so the spinner stops as
  // soon as the run flips to running/passed/failed in the DB.
  useInertiaCable(test_stream, {
    only: ["scenarios", "test_runs", "promote"],
    debounce: 250,
    onRefresh: (payload) => {
      if (payload.model !== "TestRun" && payload.model !== "TestBatch" && payload.model !== "Rehearsal") return
      router.reload({ only: ["scenarios", "test_runs", "promote"] })
      setBusyScenarioIds(new Set())
      setRunAllBusy(false)
      if (payload.model === "Rehearsal") {
        setRehearsalStarting(false)
        setRehearsalRerunning(false)
      }
    },
  })

  // Post-refactor: the URL path holds the parent Skill id (stable across
  // versions); the version is selected via `?version=<SkillVersion id>`.
  // If the selected version is already the working one, this is a no-op
  // (no server round-trip) — we just update local UI state.
  function switchVersion(versionNumber: number) {
    const target = versions.find((v) => v.version === versionNumber)
    if (!target) return
    switchVersionById(target.id)
  }

  // Timeline + VersionsTable emit SkillVersion ids directly.
  function switchVersionById(skillVersionId: number) {
    if (skillVersionId === skill.versionId) {
      const v = versions.find((x) => x.id === skillVersionId)
      if (v) setSelectedVersion(v.version)
      return
    }
    router.visit(`/skills/${skill.id}/test?version=${skillVersionId}`, { preserveScroll: false })
  }

  // "Discuss with Forge" from a live chat convo (Chat sub-tab): synthesize
  // the last few turns into a Designer composer pre-fill, then flip to
  // Design so Forge can iterate on the prompt.
  async function discussTestConvo(convoId: number) {
    try {
      const res = await fetch(`/skills/${skill.id}/test/agent_chat/discuss?convo_id=${convoId}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const data = await res.json() as { message?: string }
      if (!data.message) return
      setSeedMessage(data.message)
      setSeedNonce((n) => n + 1)
      setActiveTab("design")
    } catch {
      // Surfaced through the normal flash path on the next request.
    }
  }

  // "Fix with Forge" on a failed run: fetch the synthesized operator
  // message and seed the Design composer with it. Same backend path the
  // shipped "Discuss with Forge" button used.
  async function fixWithForge(runId: number) {
    try {
      const res = await fetch(`/skills/${skill.id}/test_runs/${runId}/discuss_payload`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const data = await res.json() as { message?: string }
      if (!data.message) return
      setSeedMessage(data.message)
      setSeedNonce((n) => n + 1)
      setActiveTab("design")
    } catch {
      // Surfaced through the normal flash path on the next request.
    }
  }

  // Run a single scenario: brand-new run if there's no latestRunId, else
  // replay the latest run against the current draft.
  async function runScenario(scenarioId: number) {
    const s = scenarios.find((x) => x.id === scenarioId)
    if (!s) return

    setBusyScenarioIds((prev) => new Set(prev).add(scenarioId))
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const headers: HeadersInit = { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf }

      if (s.latestRunId) {
        await fetch(`/skills/${skill.id}/test_runs/${s.latestRunId}/replay`, { method: "POST", credentials: "same-origin", headers })
      } else {
        await fetch(`/skills/${skill.id}/test_runs`, {
          method: "POST", credentials: "same-origin", headers,
          body: JSON.stringify({ scenario_ids: [scenarioId], agent_id: agent?.id }),
        })
      }
      // Server broadcasts → cable → reload. Busy flag cleared in onRefresh.
    } catch {
      setBusyScenarioIds((prev) => {
        const next = new Set(prev)
        next.delete(scenarioId)
        return next
      })
    }
  }

  async function runAll() {
    if (scenarios.length === 0) return
    setRunAllBusy(true)
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      await fetch(`/skills/${skill.id}/test_runs`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        body: JSON.stringify({ agent_id: agent?.id }),
      })
    } catch {
      setRunAllBusy(false)
    }
  }

  async function runRehearsal() {
    setRehearsalStarting(true)
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      await fetch(`/skills/${skill.id}/rehearsals`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
      })
      // Mirror runAll: server broadcasts → cable → reload. Spinner is cleared
      // either by the Rehearsal cable refresh or by a flash-error short-path.
      router.reload({ only: ["promote", "scenarios"] })
    } catch {
      setRehearsalStarting(false)
    }
  }

  // Resets only the failed/errored runs in an existing rehearsal — backend
  // flips the rehearsal back to running and reuses the same id.
  async function rerunFailedRehearsal(rehearsalId: number) {
    setRehearsalRerunning(true)
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const res = await fetch(`/skills/${skill.id}/rehearsals/${rehearsalId}/rerun_failed`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        console.error("rerun_failed:", data.error ?? res.statusText)
        setRehearsalRerunning(false)
        return
      }
      router.reload({ only: ["promote", "scenarios"] })
    } catch {
      setRehearsalRerunning(false)
    }
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow={<Link href="/skills" className="hover:text-app-text">← Skills</Link>}
        title={`Test Lab: ${skill.name}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <code className="rounded bg-white/5 px-1 text-xs">{skill.key}</code>
            <Badge tone={skill.live ? "gold" : skill.state === "draft" ? "red" : "neutral"}>
              v{skill.version} {skill.state}
            </Badge>
            {skill.isGlobal && <Badge tone="neutral">Global</Badge>}
            <span className="text-xs text-app-muted">
              · {skill.agentAssignments.length} agent{skill.agentAssignments.length === 1 ? "" : "s"} attached
            </span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Link
              href={`/skills/${skill.id}/edit`}
              className="rounded-control border border-border px-3 py-1.5 text-xs font-medium text-app-muted transition hover:bg-white/5 hover:text-app-text"
              title="Open the manual prompt + tools editor"
            >
              Manual edit
            </Link>
            <VersionSelector
              skillId={skill.id}
              selectedVersion={selectedVersion}
              versions={versions}
              isGlobal={skill.isGlobal}
              onSwitchVersion={switchVersion}
              canPromote={promote.canPromote}
              blockerCount={promote.blockerCount}
              rehearsal={promote.rehearsal}
            />
          </div>
        }
      />

      {/* Page-level flash removed — the global FlashBanner in AppShell
          renders props.flash already, so duplicating it here showed the
          same toast twice. */}

      <Card variant="inset" className="px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <VersionTimeline
              versions={versions}
              selectedVersionId={skill.versionId}
              onSelect={switchVersionById}
            />
          </div>
          {skill.state === "draft" && skill.versionId != null && (
            <button
              type="button"
              onClick={() => {
                if (!confirm(`Delete draft v${skill.version}? This cannot be undone.`)) return
                router.delete(`/skills/${skill.id}/versions/${skill.versionId}`)
              }}
              className="shrink-0 rounded-control border border-status-error/40 px-3 py-1.5 text-xs font-medium text-status-error transition hover:bg-status-error/10"
              title="Delete this draft version"
            >
              Delete draft
            </button>
          )}
        </div>
      </Card>

      <TestLabTabs active={activeTab} onSelect={setActiveTab} />

      {activeTab === "design" && (
        <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <DesignerPane
            skillId={skill.id}
            skillSnapshot={skill as unknown as Record<string, unknown>}
            scenarios={scenarios as unknown as Array<Record<string, unknown>>}
            isDraft={skill.state === "draft"}
            seedMessage={seedMessage}
            seedNonce={seedNonce}
            onSeedConsumed={() => setSeedMessage(null)}
          />
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-app-text">Edit History</h3>
            <EditHistoryPane entries={skill.editHistory ?? []} compact />
          </Card>
        </section>
      )}

      {activeTab === "chat" && (
        <section className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <TestPane
            skillId={skill.id}
            skillKey={skill.key}
            defaultSkillVersionId={skill.versionId}
            agents={agents}
            defaultAgentId={agent?.id ?? null}
            testStream={test_stream}
            initialSourceKey={initial_source_key}
            onDiscussConvo={discussTestConvo}
          />
          <VersionsTable
            versions={versions}
            selectedVersionId={skill.versionId}
            onSelect={switchVersionById}
            skillId={skill.id}
            skillKey={skill.key}
          />
        </section>
      )}

      {activeTab === "test" && (
        <section className="space-y-4">
          {skill.draftVersionId != null && skill.state === "draft" && (
            <RehearsalCardCompact
              rehearsal={promote.rehearsal}
              skillId={skill.id}
              draftVersionNumber={skill.version}
              onRunRehearsal={runRehearsal}
              isStarting={rehearsalStarting}
              onRerunFailed={promote.rehearsal ? () => rerunFailedRehearsal(promote.rehearsal!.id) : undefined}
              isRerunning={rehearsalRerunning}
            />
          )}
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
          <ScenarioList
            scenarios={scenarios}
            selectedId={selectedScenarioId}
            onSelect={setSelectedScenarioId}
            onRunOne={runScenario}
            onRunAll={runAll}
            busyScenarioIds={busyScenarioIds}
            runAllBusy={runAllBusy}
            skillId={skill.id}
          />
          {selectedScenario ? (
            <ScenarioDetail
              skillId={skill.id}
              scenario={selectedScenario}
              testStream={test_stream}
              onFix={() => selectedScenario.latestRunId
                ? fixWithForge(selectedScenario.latestRunId)
                : Promise.resolve()}
            />
          ) : (
            <div className="flex items-center justify-center rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
              {scenarios.length === 0
                ? "No scenarios for this skill yet."
                : "Select a scenario from the left to see the conversation and assertions."}
            </div>
          )}
          </div>
        </section>
      )}

      <div className="mt-6 flex justify-end border-t border-border pt-4 text-sm text-app-muted">
        <Link href={`/skills/${skill.id}/edit`} className="hover:text-app-text">
          View manual config →
        </Link>
      </div>
    </AppShell>
  )
}
